import { crdt } from '@w/crdt'
import type { Blocks } from '@w/content-blocks'
import { pageDocument, writeDocument } from '@w/content-blocks-editor/doc-bindings-for-page'
import { persistLocalRows, persistPendingRows, reexpressPersistedRows, type PendingRowStep, type PendingStore, exportPendingWork, importPendingWork } from '@w/db-replication/pending-rows'
import type { DatabaseDriver } from '@w/db'
import type { Tel } from '@w/tel'
import { commitPageEdits } from './commit-page-edit'
import { newPageDocument, UNTITLED_PAGE, type PageDocumentContent } from './page-document'
import { replicatedTables, type WorkspaceDb } from './workspace-tables'

/** Derived on the write path from page commits; re-expressing the pages re-derives them, so replaying would double them. */
const derivedTables: ReadonlySet<string> = new Set(['pageRelations', 'pageTranslations'])
/** The page commit owns these page columns: the CRDT value and its write stamps. */
const commitOwnedPageColumns = ['data', 'updatedAt', 'editedBy']

export function pendingStore(driver: Pick<DatabaseDriver, 'query' | 'transaction'>): PendingStore {
  return { query: (sql, params) => driver.query(sql, params), transaction: fn => driver.transaction(tx => fn({ query: (sql, params) => tx.query(sql, params) })) }
}

export { exportPendingWork, importPendingWork }

/** db-crdt-sync spec :37: persist every pending row, durably, before the replica resets (persistPendingRows). */
export function persistPendingWork(store: PendingStore): Promise<number> {
  return persistPendingRows(store, replicatedTables)
}

/** .agents/skills/em/requirements.md 2026-10-27 16:5x: after a production reset the device's pages (and the child edges
 * that place them) are its only copy; they are held as work the server lacks until the user imports them. */
export async function persistLocalWork(store: PendingStore): Promise<number> {
  // Personal roots (kind personalRoot) are recreated by the server with the membership (personalRoots), and the page home creates untouched
  // (flashcards-fe.router.tsx createEmptyPage) holds nothing the user wrote (lead decision 2026-10-27): neither is data.
  const pages = await store.query<{ id: string; title: string | null; data: Uint8Array }>(`SELECT id,title,data FROM pages WHERE kind<>'personalRoot'`)
  const ids = pages.filter(page => hasUserContent(page)).map(page => `'${page.id.replaceAll("'", "''")}'`).join(',')
  if (ids === '') return 0
  return persistLocalRows(store, replicatedTables, { pages: `id IN (${ids})`, pageRelations: `type='child' AND toPageId IN (${ids})` })
}

/** A page carries user content unless its title is empty or the default one and its body has no text and no block other than an empty paragraph. */
export function hasUserContent(page: { title: string | null; data: Uint8Array }): boolean {
  if (page.title && page.title !== UNTITLED_PAGE) return true
  return blocksOf(new Uint8Array(page.data)).some(block => block.type !== 'p' || hasText(block))
}

function hasText(node: unknown): boolean {
  if (Array.isArray(node)) return node.some(item => hasText(item))
  if (typeof node !== 'object' || node === null) return false
  return Object.entries(node).some(([key, value]) => key === 'text' && typeof value === 'string' ? value.trim() !== '' : hasText(value))
}

/**
 * db-crdt-sync spec :37 (a reset never loses work): after the next successful open from the snapshot, write the
 * persisted work onto it exactly once, as new writes by the device's user at the snapshot's epoch.
 * 1. Each pending page's CRDT value, written through the product's view writer (writeDocument, the markdown patch
 *    editor's path; the facade exposes no value-to-ops diff, so it stays here). The local doc's history is merged
 *    into the server value on a throwaway copy: ops the server already holds (a push accepted before its receipt
 *    arrived, DST seed 1024) change nothing, so only what the server lacks is written, once. When the server's
 *    epoch no longer translates the local one (the freeze map was dropped: StaleEpoch), the base→local diff is
 *    used instead: the snapshot is brought to `base` on a throwaway copy, then `base` to `local`. Either way only
 *    the last edit is committed, so a peer's concurrent edit stays, merged by the CRDT. A page with no base that the server has (pushed, never
 *    acknowledged) takes the server value as base; one the server lacks is created from its local value. The
 *    commit marks the row's step done in the same transaction.
 * 2. Every other pending row and the page scalar columns: changed columns only, through db-replication.
 */
export function reexpressPendingWork(db: WorkspaceDb, store: PendingStore, input: { userId: string; spaceId?: string; personalRootId: string; now: Date; tel: Tel }) {
  const pages: PendingRowStep = async (row, done, rows) => {
    const local = row.local
    if (!local) return
    const pageId = String(local.id)
    const localData = local.data as Uint8Array
    const [remote] = await store.query<{ data: Uint8Array }>('SELECT data FROM pages WHERE id=?', [pageId])
    if (remote) {
      const baseData = row.base?.data instanceof Uint8Array ? row.base.data : remote.data
      const frame = merged(remote.data, localData, input.userId) ?? rebase(remote.data, baseData, localData, input.userId)
      if (frame === 'held') await store.query(done.sql, done.params)
      else await commitPageEdits(db, [{ pageId, userId: input.userId, frame, now: input.now, tel: input.tel }], { also: [done] })
    } else {
      const parentId = await placement(rows, pageId)
      const create = (frame: Uint8Array) => commitPageEdits(db, [{ pageId, userId: input.userId, frame, now: input.now, tel: input.tel,
        ...(input.spaceId ? { spaceId: input.spaceId } : {}), ...(parentId === undefined ? {} : { parentId }) }], { also: [done] })
      try { await create(localData) }
      catch (error) {
        // Lead decision 2026-10-27: an import never fails. History another user authored is refused under this user
        // ("change … is not admitted for User"); the page is created as a fresh document of its current value,
        // authored by this user. The server's history of it was deleted by the production reset.
        if (!(error instanceof Error) || !error.message.includes('is not admitted for User')) throw error
        const fresh = newPageDocument({ title: String(local.title ?? ''), content: freshContent(localData), deletedAt: null }, input.userId)
        try { await create(fresh.encodeSnapshot().bytes) } finally { fresh.free() }
      }
    }
  }
  /** The persisted parent, unless the reset removed it: a personal root is minted anew per membership (personalRoots,
   * db-crdt-sync.spec.md root-pages-have-ulid-ids) and the reset drops every other page the server held, so a page whose
   * parent is neither persisted nor in the snapshot goes under the user's current personal root, the directory's
   * `personalRootId` (never inferred from grants: a user can hold Full on several roots); an import never fails. */
  async function placement(rows: Parameters<PendingRowStep>[2], pageId: string): Promise<string | undefined> {
    const edge = rows.find(other => other.table === 'pageRelations' && other.local?.toPageId === pageId && other.local.type === 'child')
    if (!edge) return undefined
    const parentId = String(edge.local?.fromPageId)
    if (rows.some(other => other.table === 'pages' && other.local?.id === parentId)) return parentId
    if ((await store.query('SELECT 1 FROM pages WHERE id=?', [parentId])).length > 0) return parentId
    return input.personalRootId
  }
  return reexpressPersistedRows(store, replicatedTables, { steps: { pages }, skip: derivedTables, exclude: { pages: commitOwnedPageColumns } })
}

/** The edit that brings `remote` to remote ∪ local history; 'held' when the server already holds all of it; null
 * when local's epoch no longer translates onto remote's. */
function merged(remote: Uint8Array, local: Uint8Array, user: string): Uint8Array | 'held' | null {
  const union = crdt.load(remote, { user })
  let target: Blocks
  try {
    try { union.applyUpdate(crdt.historyUpdate(local).raw()) }
    catch (error) { if (error instanceof Error && error.message.includes('StaleEpoch')) return null; throw error }
    target = pageDocument(union).content.children as unknown as Blocks
  } finally { union.free() }
  const current = blocksOf(remote)
  if (JSON.stringify(target) === JSON.stringify(current)) return 'held'
  const writer = crdt.load(remote, { user })
  try { return writer.edit(tx => { writeDocument(tx, structuredClone(target), current) }).update.bytes } finally { writer.free() }
}

function rebase(remote: Uint8Array, base: Uint8Array, local: Uint8Array, user: string): Uint8Array {
  const baseBlocks = blocksOf(base), localBlocks = blocksOf(local)
  // The revert to `base` is authored by a throwaway replica, so the committed edit never depends on its clocks.
  const reverted = crdt.load(remote, { user })
  let atBase: Uint8Array
  try {
    reverted.edit(tx => { writeDocument(tx, structuredClone(baseBlocks), blocksOf(remote)) })
    atBase = reverted.encodeSnapshot().bytes
  } finally { reverted.free() }
  const scratch = crdt.load(atBase, { user })
  try {
    return scratch.edit(tx => { writeDocument(tx, structuredClone(localBlocks), baseBlocks) }).update.bytes
  } finally { scratch.free() }
}

/** The page's current content (its CRDT structure; kind is a replicated column, re-applied with the row). */
function freshContent(bytes: Uint8Array): PageDocumentContent {
  const doc = crdt.load(bytes)
  try { return { kind: 'note', ...structuredClone(pageDocument(doc).content) } as unknown as PageDocumentContent }
  finally { doc.free() }
}

function blocksOf(bytes: Uint8Array): Blocks {
  const doc = crdt.load(bytes)
  try { return pageDocument(doc).content.children as unknown as Blocks }
  finally { doc.free() }
}
